import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";
import { WEATHER_IDS, WEATHER_TERMS } from "@nav-games/weather";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface FlashcardsProps {
  /// `forward`: show the term, pick the range. `reverse`: show the
  /// range, pick the term. Single component, two routes — saves a
  /// big duplicate file.
  direction: "forward" | "reverse";
  onNavigate: (to: string) => void;
}

const CARDS_PER_SESSION = 15;
const BEAT_THE_CLOCK_MS = 90_000;
const CHOICES_PER_CARD = 4;
// Recent answers kept out of the next draw so the spaced-repetition
// picker doesn't resurface the same card within a short window.
// Clamped to pool size - 1 so there is always an eligible card.
const AVOID_WINDOW = Math.min(8, WEATHER_IDS.length - 1);

type SessionMode = "cards" | "timed";

interface Card {
  answer: string;
  choices: readonly string[];
}

function nextCard(module: string, recent: readonly string[]): Card {
  const answer = pickWeightedKey(module, WEATHER_IDS, recent);
  const answerCategory = WEATHER_TERMS[answer]!.category;
  // Prefer distractors from the same category so the buttons are
  // semantically plausible (a wind force shouldn't sit next to a
  // sea-state label).
  const sameCategory = WEATHER_IDS.filter(
    (id) => id !== answer && WEATHER_TERMS[id]!.category === answerCategory,
  );
  const distractors = new Set<string>();
  while (
    distractors.size < CHOICES_PER_CARD - 1 &&
    sameCategory.length > distractors.size
  ) {
    const d = sameCategory[Math.floor(Math.random() * sameCategory.length)]!;
    if (d !== answer) distractors.add(d);
  }
  // If the category is too small (timing has 3 entries), fill the
  // remaining slots from across the catalogue.
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = WEATHER_IDS[Math.floor(Math.random() * WEATHER_IDS.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Flashcards({ direction, onNavigate }: FlashcardsProps) {
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  if (!sessionMode) {
    return (
      <Picker
        direction={direction}
        onStart={setSessionMode}
        onBack={() => onNavigate("/play/weather")}
      />
    );
  }
  return (
    <Game
      direction={direction}
      sessionMode={sessionMode}
      onAgain={() => setSessionMode(sessionMode)}
      onBack={() => onNavigate("/play/weather")}
    />
  );
}

function Picker({
  direction,
  onStart,
  onBack,
}: {
  direction: "forward" | "reverse";
  onStart: (m: SessionMode) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Weather flashcards ({direction === "forward" ? "term → meaning" : "range → term"})</h2>
      <div className="card">
        <h3>Session length</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("cards")}>{CARDS_PER_SESSION} cards</button>
          <button className="secondary" onClick={() => onStart("timed")}>
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} seconds
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Weather
      </button>
    </>
  );
}

interface GameProps {
  direction: "forward" | "reverse";
  sessionMode: SessionMode;
  onAgain: () => void;
  onBack: () => void;
}

function Game({ direction, sessionMode, onAgain, onBack }: GameProps) {
  const MODULE = `weather-${direction}`;
  const GAME = "flashcards";
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode, MODULE],
  );

  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard(MODULE, []);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(MODULE, recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-AVOID_WINDOW);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [MODULE, index, recorder, sessionMode]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 700 : 1800);
    },
    [advance, card, feedback, recorder, result, shownAt, MODULE],
  );

  if (result) {
    return <EndCard module={MODULE} result={result} onAgain={onAgain} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const answerSpec = WEATHER_TERMS[card.answer]!;
  const prompt = direction === "forward" ? answerSpec.term : answerSpec.shortMeaning;
  const buttonLabel = (id: string) => {
    const spec = WEATHER_TERMS[id]!;
    return direction === "forward" ? spec.shortMeaning : spec.term;
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "weather" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">{header}</p>
      <div className="card" style={{ textAlign: "center" }}>
        <p
          style={{
            fontSize: 26,
            margin: "16px 0 24px",
            minHeight: "1.4em",
            fontWeight: 600,
          }}
        >
          {prompt}
        </p>
        {feedback && (
          <p className="small muted" style={{ marginTop: 0 }}>
            {feedback === "right" ? "✓ " : "✗ "}
            <strong>{answerSpec.term}</strong> — {answerSpec.meaning}
          </p>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 16,
          }}
        >
          {card.choices.map((id) => (
            <button
              key={id}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(id)}
              style={{
                fontSize: 14,
                padding: "12px 8px",
                textAlign: "left",
                background:
                  feedback && id === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {buttonLabel(id)}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  module,
  result,
  onAgain,
  onBack,
}: {
  module: string;
  result: SessionResult;
  onAgain: () => void;
  onBack: () => void;
}) {
  const rolling = WEATHER_IDS
    .map((id) => ({ id, ...statsFor(module, id) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}.
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest terms (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.id}>
                <strong>{WEATHER_TERMS[w.id]?.term ?? w.id}</strong> —{" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Weather
        </button>
      </div>
    </>
  );
}
